import { infiniteQueryOptions, keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { orgKey } from '@/app/queryClient';
import { snippetOf } from './snippet';

type S = components['schemas'];
export type Conversation = S['Conversation'];
export type ConversationState = S['ConversationState'];
export type SenderType = S['SenderType'];
export type Message = S['Message'];
export type MessageAddress = S['MessageAddress'];
export type ResolutionCase = S['ThreadResolutionCase'];
export type ResolutionReason = S['ThreadResolutionReasonCode'];
export type Draft = S['Draft'];
export type Location = S['Location'];
export type Awaiting = S['ConversationAwaiting'];

export const RESOLUTION_REASON_LABELS: Record<ResolutionReason, string> = {
  unknown_sender: 'Unknown sender',
  ambiguous_reference: 'Ambiguous reference',
  multiple_candidates: 'Several matches',
  scope_change: 'Scope change',
  identity_conflict: 'Identity conflict',
};

/** All includes inert recorded correspondence without manufacturing a To do. */
export type ConversationView = 'chat' | 'history' | 'needs' | 'waiting' | 'all' | 'closed';
export const DEFAULT_CONVERSATION_VIEW: ConversationView = 'chat';
export const CONVERSATION_VIEWS: Array<{ id: ConversationView; label: string }> = [
  { id: 'chat', label: 'Chats' },
  { id: 'history', label: 'History / Notes' },
  { id: 'needs', label: 'To do' },
  { id: 'waiting', label: 'Awaiting reply' },
  { id: 'all', label: 'All' },
  { id: 'closed', label: 'Closed' },
];

type ListQuery = { view?: 'chat' | 'history' | 'all'; awaiting?: Awaiting; has_pending_draft?: boolean; state?: ConversationState };

/** One server list behind a view, with an optional client filter for what the API cannot express. */
interface ViewPart {
  query: ListQuery;
  keep?: (c: Conversation) => boolean;
}

const isOpen = (c: Conversation) => c.state !== 'closed' && c.state !== 'archived';

/**
 * Server filters per view. The API has no OR, so a view can merge several lists by latest message
 * (each keeps its own cursor). Needs you: they replied and nothing is proposed yet, or a proposed
 * reply waits for approval. Waiting on them: we wrote last, or our approved reply is about to go.
 */
const VIEW_QUERY: Record<ConversationView, ViewPart[]> = {
  chat: [{ query: { view: 'chat' } }],
  history: [{ query: { view: 'history' } }],
  all: [{ query: {} }],
  needs: [
    { query: { awaiting: 'us', has_pending_draft: false }, keep: isOpen },
    { query: { has_pending_draft: true }, keep: (c) => isOpen(c) && c.pending_draft?.state === 'pending_review' },
  ],
  waiting: [
    { query: { awaiting: 'them', has_pending_draft: false }, keep: isOpen },
    { query: { has_pending_draft: true }, keep: (c) => isOpen(c) && c.pending_draft?.state === 'scheduled' },
  ],
  closed: [{ query: { state: 'closed' } }],
};

type Cursors = Array<string | null>;

export function conversationListQuery(orgId: string, view: ConversationView, q: string | undefined) {
  const parts = VIEW_QUERY[view];
  return infiniteQueryOptions({
    queryKey: orgKey(orgId, 'conversations', 'list', { view, q: q ?? null }),
    initialPageParam: parts.map(() => '') as Cursors,
    placeholderData: keepPreviousData,
    getNextPageParam: (last: { items: Conversation[]; next: Cursors }) => (last.next.some((c) => c !== null) ? last.next : undefined),
    queryFn: async ({ signal, pageParam }) => {
      const pages = await Promise.all(
        parts.map((part, i) =>
          pageParam[i] === null
            ? null
            : unwrap(
                api.GET('/api/v1/orgs/{orgId}/conversations', {
                  params: { path: { orgId }, query: { ...part.query, q, sort: '-last_message_at', cursor: pageParam[i] || undefined, limit: 50 } },
                  signal,
                }),
              ),
        ),
      );
      const seen = new Set<string>();
      const items = pages
        .flatMap((p, i) => (p?.items ?? []).filter((c) => parts[i]?.keep?.(c) ?? true))
        .filter((c) => c.state !== 'archived' && !seen.has(c.id) && Boolean(seen.add(c.id)))
        .sort((a, b) => (b.last_message_at ?? '').localeCompare(a.last_message_at ?? ''));
      return { items, next: pages.map((p) => (p?.page.has_more ? (p.page.next_cursor ?? null) : null)) };
    },
  });
}

export function conversationQuery(orgId: string, conversationId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'conversations', 'detail', conversationId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/conversations/{conversationId}', {
          params: { path: { orgId, conversationId } },
          signal,
        }),
      ),
  });
}

/** Timeline pages, oldest first; later pages append below. */
export function messagesQuery(orgId: string, conversationId: string) {
  return infiniteQueryOptions({
    queryKey: orgKey(orgId, 'conversations', 'messages', conversationId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: S['MessagePage']) => (last.page.has_more ? (last.page.next_cursor ?? undefined) : undefined),
    queryFn: ({ signal, pageParam }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/conversations/{conversationId}/messages', {
          params: { path: { orgId, conversationId }, query: { sort: 'occurred_at', cursor: pageParam, limit: 100 } },
          signal,
        }),
      ),
  });
}

export function messageQuery(orgId: string, messageId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'messages', messageId),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/messages/{messageId}', { params: { path: { orgId, messageId } }, signal })),
  });
}

export function rawMessageQuery(orgId: string, messageId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'messages', messageId, 'raw'),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/messages/{messageId}/raw', {
          params: { path: { orgId, messageId } },
          parseAs: 'text',
          signal,
        }),
      ) as Promise<string>,
  });
}

export function locationNameQuery(orgId: string, locationId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'locations', 'detail', locationId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/locations/{locationId}', { params: { path: { orgId, locationId } }, signal }),
      ),
    staleTime: 60_000,
  });
}

export function locationSearchQuery(orgId: string, q: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'locations', 'list', { q }, null, 8),
    enabled: q.trim().length > 0,
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/locations', { params: { path: { orgId }, query: { q, limit: 8 } }, signal })),
  });
}

/** Top bar search: a few conversations matching the text, newest first. */
export function conversationSearchQuery(orgId: string, q: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'conversations', 'list', { search: q }),
    enabled: q.trim().length > 0,
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/conversations', { params: { path: { orgId }, query: { q, sort: '-last_message_at', limit: 6 } }, signal })),
    staleTime: 30_000,
  });
}

/** People linked to a location, with their primary email. */
export function locationContactsQuery(orgId: string, locationId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'contacts', 'list', { location_id: locationId }),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/contacts', {
          params: { path: { orgId }, query: { location_id: locationId, limit: 50 } },
          signal,
        }),
      ),
    staleTime: 60_000,
  });
}

export function contactQuery(orgId: string, contactId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'contacts', 'detail', contactId),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/v1/orgs/{orgId}/contacts/{contactId}', { params: { path: { orgId, contactId } }, signal })),
    staleTime: 5 * 60_000,
  });
}

export function locationConversationsQuery(orgId: string, locationId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'conversations', 'list', { location_id: locationId }),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/conversations', {
          params: { path: { orgId }, query: { location_id: locationId, sort: '-last_message_at', limit: 20 } },
          signal,
        }),
      ),
  });
}

export function resolutionCasesQuery(orgId: string, state: string | undefined, cursor: string | undefined, limit: number) {
  return queryOptions({
    queryKey: orgKey(orgId, 'thread-resolution-cases', 'list', state ?? null, cursor ?? null, limit),
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/thread-resolution-cases', {
          params: {
            path: { orgId },
            query: { state: state as S['ThreadResolutionCaseState'] | undefined, cursor, limit },
          },
          signal,
        }),
      ),
  });
}

export function resolutionCaseQuery(orgId: string, caseId: string) {
  return queryOptions({
    queryKey: orgKey(orgId, 'thread-resolution-cases', 'detail', caseId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/thread-resolution-cases/{caseId}', { params: { path: { orgId, caseId } }, signal }),
      ),
  });
}

const COUNT_REFRESH = 30_000;

/** Open routing cases shown at the top of Needs you (and counted in the top bar). */
export const UNASSIGNED_LIMIT = 20;

/**
 * Top bar count of what needs you: replies waiting on us without a proposed reply, proposed
 * replies waiting for approval, and incoming mail nobody has placed in a conversation yet.
 * Undefined until all three are known.
 */
export function useNeedsYouCount(orgId: string): number | undefined {
  const replies = useQuery({
    queryKey: orgKey(orgId, 'conversations', 'list', { count: 'awaiting_us_without_draft' }),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/conversations', {
          params: { path: { orgId }, query: { awaiting: 'us', has_pending_draft: false, limit: 50, include_total: true } },
          signal,
        }),
      ),
    select: (p) => p.page.total ?? p.items.filter((c) => c.state !== 'archived').length,
    refetchInterval: COUNT_REFRESH,
  });
  const drafts = useQuery({
    queryKey: orgKey(orgId, 'drafts', 'list', { count: 'pending_review' }),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/v1/orgs/{orgId}/drafts', {
          params: { path: { orgId }, query: { state: 'pending_review', limit: 50, include_total: true } },
          signal,
        }),
      ),
    select: (p) => p.page.total ?? p.items.length,
    refetchInterval: COUNT_REFRESH,
  });
  const unassigned = useQuery({
    ...resolutionCasesQuery(orgId, 'open', undefined, UNASSIGNED_LIMIT),
    select: (p) => p.page.total ?? p.items.length,
    refetchInterval: 60_000,
  });
  if (replies.data === undefined || drafts.data === undefined || unassigned.data === undefined) return undefined;
  return replies.data + drafts.data + unassigned.data;
}

/** Invalidation roots after a conversation command. */
export function conversationKeys(orgId: string, conversationId?: string) {
  return [
    orgKey(orgId, 'conversations', 'list'),
    ...(conversationId
      ? [orgKey(orgId, 'conversations', 'detail', conversationId), orgKey(orgId, 'conversations', 'messages', conversationId)]
      : []),
  ];
}

export function addressesOf(m: Message, kind: MessageAddress['kind']): MessageAddress[] {
  return m.addresses.filter((a) => a.kind === kind).sort((a, b) => a.position - b.position);
}

export function formatAddress(a: { display_name?: string | null; email_original: string }): string {
  return a.display_name ? `${a.display_name} <${a.email_original}>` : a.email_original;
}

// ---------- List rows ----------

export interface ConversationRow {
  conversation: Conversation;
  name: string;
  company: string | null;
  subject: string;
  locationId: string | null;
  /** Their last words without the greeting; the subject when there is no mail yet. */
  gist: string;
  at: string | null;
  unread: boolean;
}

export function toRow(c: Conversation): ConversationRow {
  const cp = c.counterpart;
  return {
    conversation: c,
    name: cp ? cp.name || cp.email : c.source_history ? c.source_history.company_name || 'History / Notes' : 'No recipient',
    company: c.source_history ? 'Recorded company history' : cp?.company_name ?? null,
    subject: c.title,
    locationId: c.primary_location_id,
    gist: (c.latest_message?.snippet ? snippetOf(c.latest_message.snippet) : null) || c.source_history?.snippet || c.title,
    at: c.source_history ? c.source_history.occurred_at : c.last_message_at ?? c.updated_at,
    unread: c.unread_count > 0,
  };
}

/**
 * Drops a leading "Place name:" from a title when it names the location, so a row never says the
 * location twice ("Clondalkin Unit 9: 38 kV connection" at "Clondalkin Business Park Unit 9").
 */
export function subjectWithoutLocation(subject: string, locationName: string | null | undefined): string {
  if (!locationName) return subject;
  const colon = subject.indexOf(':');
  if (colon <= 0) return subject;
  const rest = subject.slice(colon + 1).trim();
  if (!rest) return subject;
  const words = (s: string) => new Set(s.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 4));
  const place = words(locationName);
  for (const w of words(subject.slice(0, colon))) if (place.has(w)) return rest.charAt(0).toUpperCase() + rest.slice(1);
  return subject;
}

// ---------- Message text ----------

/** A closing line on its own ("Best,", "Kind regards", "Met vriendelijke groet"): what follows is a signature. */
const SIGN_OFF =
  /^(best|best regards|kind regards|regards|warm regards|many thanks|thanks|thank you|cheers|all the best|sincerely|yours sincerely|met vriendelijke groet(en)?|mit freundlichen gr(ü|ue)(ß|ss)en|viele gr(ü|ue)(ß|ss)e|med vänlig hälsning|vänliga hälsningar|med vennlig hilsen|hilsen|cordialement|bien à vous|saludos)[,.!]?$/;

export interface MessageParts {
  body: string;
  /** Signature and quoted history, collapsed by default. */
  hidden: string | null;
}

/**
 * Splits plain text into what is new and what is quoted history or signature. Only a trailing
 * block is hidden so a new sentence is never lost: a "On ... wrote:" / "> " tail, a "-- " signature,
 * or a short last paragraph that starts with the sender's name or a sign-off.
 */
export function splitMessage(text: string, senderName?: string | null): MessageParts {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let end = lines.length;
  // Quoted tail.
  for (let i = 0; i < lines.length; i++) {
    const l = (lines[i] ?? '').trim();
    if (/^On .+wrote:$/.test(l) || /^-{2,}\s*Original Message\s*-{2,}$/i.test(l)) {
      if (lines.slice(i + 1).every((x) => x.trim() === '' || x.trim().startsWith('>'))) {
        end = i;
        break;
      }
    }
  }
  if (end === lines.length) {
    let i = lines.length;
    while (i > 0 && ((lines[i - 1] ?? '').trim() === '' || (lines[i - 1] ?? '').trim().startsWith('>'))) i--;
    if (lines.slice(i).some((l) => l.trim().startsWith('>'))) end = i;
  }
  const head = lines.slice(0, end);
  while (head.length && (head[head.length - 1] ?? '').trim() === '') head.pop();
  // Signature: "-- " delimiter, or the last short paragraph starting with the sender's name.
  let sigStart = head.findIndex((l) => l === '-- ' || l === '--');
  if (sigStart < 0) {
    const lastBlank = head.map((l) => l.trim()).lastIndexOf('');
    const para = head.slice(lastBlank + 1);
    const first = (para[0] ?? '').trim().toLowerCase();
    const name = senderName?.trim().toLowerCase();
    const firstName = name?.split(/\s+/)[0] ?? '';
    const byName = Boolean(name && (first.startsWith(name) || (firstName.length >= 2 && (first === firstName || first.startsWith(`${firstName} `)))));
    if (lastBlank > 0 && para.length > 0 && para.length <= 4 && para.every((l) => l.length <= 60) && (byName || SIGN_OFF.test(first))) {
      sigStart = lastBlank + 1;
    }
  }
  const visible = sigStart > 0 ? head.slice(0, sigStart) : head;
  while (visible.length && (visible[visible.length - 1] ?? '').trim() === '') visible.pop();
  const hiddenLines = [...(sigStart > 0 ? head.slice(sigStart) : []), ...(end < lines.length ? ['', ...lines.slice(end)] : [])];
  const hidden = hiddenLines.join('\n').trim();
  return { body: visible.join('\n'), hidden: hidden || null };
}
